// The OAuth sign-in a person was in the middle of when PAC sent them to the login page.
//
// Kept in sessionStorage so it survives the login and MFA screens, and dropped after
// RETURN_TTL_MS so an abandoned sign-in does not hijack a later, ordinary login.

const RETURN_KEY = 'pac:post-login-return'
const RETURN_TTL_MS = 30 * 60 * 1000

function read() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null')
    if (!saved || !saved.path || Date.now() - saved.at > RETURN_TTL_MS) return null
    return saved.path
  } catch (e) {
    return null
  }
}

/**
 * Remembers the authorize request to come back to after signing in. `continue=1` is set on it:
 * the person will just have signed in, so on return the app is continued to without asking again
 * (unlike `prompt=none`, a failure is still shown to them rather than handed back to the app).
 */
export function saveOauthReturn(query) {
  try {
    const params = new URLSearchParams({ ...query, continue: '1' })
    sessionStorage.setItem(RETURN_KEY, JSON.stringify({ path: `/oauth/authorize?${params.toString()}`, at: Date.now() }))
  } catch (e) { /* storage unavailable: the person lands on the home page instead */ }
}

export function clearOauthReturn() {
  try {
    sessionStorage.removeItem(RETURN_KEY)
  } catch (e) { /* nothing to clear */ }
}

/** The saved path, removed so it is followed once. */
export function takeOauthReturn() {
  const path = read()
  clearOauthReturn()
  return path
}

/** The pending request's query (client_id, redirect_uri, state…), or null. */
export function pendingOauthRequest() {
  const path = read()
  if (!path) return null
  return Object.fromEntries(new URLSearchParams(path.split('?')[1] || ''))
}

/**
 * The address that hands control back to the application with an error. Only call it with a
 * redirect_uri that /oauth/client has accepted for that client.
 */
export function oauthErrorUrl(query, error) {
  const url = new URL(query.redirect_uri)
  url.searchParams.set('error', error)
  if (query.state) url.searchParams.set('state', query.state)
  return url.toString()
}

/**
 * What the application receives, in the person's terms. Matches what the token endpoint actually
 * issues (see OauthController.issueTokens and userinfo), which is the same whatever scopes are
 * requested; keep the two in step if that changes.
 */
export function sharedDataDescriptions() {
  return [
    'Your email address',
    'Your PAC account ID and member profile IDs (including minister and staff IDs)',
    'Keeping you signed in for up to 30 days, until you sign out',
  ]
}
